import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUT_LABELS, STATUT_COLORS } from "@/lib/prospects";
import { ProspectDetail } from "@/components/ProspectDetail";
import { ImportCsvDialog } from "@/components/ImportCsvDialog";
import { AddProspectDialog } from "@/components/AddProspectDialog";
import { LaunchCampaignDialog } from "@/components/LaunchCampaignDialog";
import { SireneSearchDialog } from "@/components/SireneSearchDialog";
import { downloadExport } from "@/components/TemplatesDialog";
import { Plus, Upload, Rocket, Trash2, RefreshCw, Landmark, Download } from "lucide-react";

export default function Prospects() {
  const { user } = useAuth();
  const canWrite = ["admin", "responsable_commercial", "commercial"].includes(user?.role);
  const [prospects, setProspects] = useState(null);
  const [counts, setCounts] = useState({});
  const [filters, setFilters] = useState({ statut: "tous", q: "" });
  const [detail, setDetail] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showLaunch, setShowLaunch] = useState(false);
  const [showSirene, setShowSirene] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (filters.statut !== "tous") params.statut = filters.statut;
    if (filters.q) params.q = filters.q;
    api.get("/prospects", { params }).then(({ data }) => setProspects(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/prospects/statuts").then(({ data }) => setCounts(data)).catch(() => {});
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le prospect « ${p.raison_sociale} » ?`)) return;
    try {
      await api.delete(`/prospects/${p.id}`);
      toast.success("Prospect supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="prospects-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold sm:text-4xl">Prospects</h1>
          <p className="mt-1 text-sm text-muted-foreground">Base de prospection — anti-doublons et exclusions appliqués à l'import.</p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowAdd(true)} data-testid="add-prospect-button"><Plus size={15} className="mr-1.5" /> Ajouter</Button>
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-csv-button"><Upload size={15} className="mr-1.5" /> Importer CSV</Button>
            <Button variant="outline" onClick={() => setShowSirene(true)} data-testid="sirene-search-open-button"><Landmark size={15} className="mr-1.5" /> Rechercher (SIRENE)</Button>
            <Button onClick={() => setShowLaunch(true)} className="bg-accent hover:bg-accent/90" data-testid="launch-campaign-button">
              <Rocket size={15} className="mr-1.5" /> Lancer une campagne
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={filters.statut} onValueChange={(v) => setFilters({ ...filters, statut: v })}>
          <SelectTrigger className="w-[210px]" data-testid="prospects-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous les statuts</SelectItem>
            {Object.entries(STATUT_LABELS).map(([k, label]) => (
              <SelectItem key={k} value={k}>{label} {counts[k] ? `(${counts[k]})` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input className="w-56" placeholder="Rechercher une entreprise…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="prospects-search-input" />
        <Button variant="ghost" size="icon" onClick={load} title="Actualiser" data-testid="prospects-refresh-button"><RefreshCw size={15} /></Button>
        <Button variant="outline" size="sm" onClick={() => downloadExport("/export/prospects.xlsx", "renforteo_prospects.xlsx")} data-testid="export-prospects-button">
          <Download size={14} className="mr-1" /> Excel
        </Button>
        <span className="ml-auto text-sm text-muted-foreground">{prospects?.length ?? "…"} prospect(s)</span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="hidden grid-cols-12 gap-2 border-b bg-muted/50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground md:grid">
            <span className="col-span-3">Entreprise</span>
            <span className="col-span-2">Secteur</span>
            <span className="col-span-2">Ville</span>
            <span className="col-span-2">Contact</span>
            <span className="col-span-2">Statut</span>
            <span className="col-span-1"></span>
          </div>
          <div className="divide-y" data-testid="prospects-list">
            {prospects === null && <p className="p-6 text-sm text-muted-foreground">Chargement…</p>}
            {prospects?.length === 0 && <p className="p-6 text-sm text-muted-foreground">Aucun prospect. Importez un fichier CSV ou ajoutez une entreprise.</p>}
            {(prospects || []).map((p) => (
              <div
                key={p.id}
                className="grid cursor-pointer grid-cols-2 items-center gap-2 px-4 py-3 text-sm hover:bg-secondary/40 md:grid-cols-12"
                onClick={() => setDetail(p.id)}
                data-testid={`prospect-row-${p.id}`}
              >
                <span className="col-span-2 truncate font-medium md:col-span-3">{p.raison_sociale}</span>
                <span className="col-span-1 truncate text-muted-foreground md:col-span-2">{p.secteur || "—"}</span>
                <span className="col-span-1 truncate text-muted-foreground md:col-span-2">{p.ville || "—"}</span>
                <span className="col-span-1 truncate text-muted-foreground md:col-span-2">{p.contact_prenom} {p.contact_nom}</span>
                <span className="col-span-1 md:col-span-2"><Badge className={STATUT_COLORS[p.statut]}>{STATUT_LABELS[p.statut]}</Badge></span>
                <span className="col-span-2 flex justify-end md:col-span-1">
                  {canWrite && (
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(p); }} data-testid={`delete-prospect-${p.id}`}>
                      <Trash2 size={14} className="text-destructive" />
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {detail && <ProspectDetail prospectId={detail} onClose={() => setDetail(null)} onChanged={load} />}
      <ImportCsvDialog open={showImport} onClose={() => setShowImport(false)} onImported={load} />
      <SireneSearchDialog open={showSirene} onClose={() => setShowSirene(false)} onImported={load} />
      <AddProspectDialog open={showAdd} onClose={() => setShowAdd(false)} onAdded={load} />
      <LaunchCampaignDialog open={showLaunch} onClose={() => setShowLaunch(false)} onLaunched={() => setTimeout(load, 2500)} />
    </div>
  );
}
